import { computed, Injectable, Signal, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AppFinderApiService } from '../appfinder-service/app-finder-api.service';
import { CollectionApiService } from '../collection-service/collection-api.service';
import { SearchCriteria } from 'src/app/core/interfaces/search-criteria.interface';
import { CollectionUIService } from 'src/app/pages/start-page/services/collection-ui.service';
import { FolderUiService } from 'src/app/pages/start-page/services/folder-ui.service';
import { WidgetUIService } from 'src/app/pages/start-page/services/widget-ui.service';
import { AppInterface } from '../../interfaces/app.interface';
import { CollectionFinderItemDTO } from '../../interfaces/collection.interface';
import { StartPageSearchResults } from '../../interfaces/start-page-search-results.interface';
import { AppsSearchService } from '../apps-search-service/apps-search.service';
import { AppDescriptionCriteria } from '../apps-search-service/criterias/app-description-criteria';
import { AppKeywordsCriteria } from '../apps-search-service/criterias/app-keywords-criteria';
import { AppNameCriteria } from '../apps-search-service/criterias/app-name-criteria';
import { AppOwnerCriteria } from '../apps-search-service/criterias/app-owner-criteria';
import { AppSubtitleCriteria } from '../apps-search-service/criterias/app-subtitle-criteria';
import { AppTargetGoupsCriteria } from '../apps-search-service/criterias/app-target-groups-criteria';
import { AppTitleCriteria } from '../apps-search-service/criterias/app-title-criteria';
import { SearchableAppInterface, StartPageSearchableAppInterface } from '../apps-search-service/searchable-app.interface';
import { CollectionsSearchService } from '../collections-search-service/collections-search.service';
import { CollectionDescriptionCriteria } from '../collections-search-service/criterias/collection-description-criteria';
import { CollectionKeywordsCriteria } from '../collections-search-service/criterias/collection-keywords-criteria';
import { CollectionOwnerCriteria } from '../collections-search-service/criterias/collection-owner-criteria';
import { CollectionSubtitleCriteria } from '../collections-search-service/criterias/collection-subtitle-criteria';
import { CollectionTargetGroupsCriteria } from '../collections-search-service/criterias/collection-target-groups-criteria';
import { CollectionTitleCriteria } from '../collections-search-service/criterias/collection-title-criteria';
import { SearchableCollectionInterface } from '../collections-search-service/searchable-collection.interface';
import { PlatformService } from '../platform-service/platform.service';

@Injectable({
  providedIn: 'root',
})
export class StartPageSearchService {
  private readonly appTranslations = signal(new Map<string, string[]>());
  private readonly collectionTranslations = signal(new Map<string, string[]>());
  private translationsRequested = false;

  private readonly appSearchCriteria: SearchCriteria[] = [
    new AppTitleCriteria(),
    new AppNameCriteria(),
    new AppDescriptionCriteria(),
    new AppSubtitleCriteria(),
    new AppKeywordsCriteria(),
    new AppTargetGoupsCriteria(),
    new AppOwnerCriteria()
  ];
  private readonly collectionSearchCriteria: SearchCriteria[] = [
    new CollectionTitleCriteria(),
    new CollectionSubtitleCriteria(),
    new CollectionKeywordsCriteria(),
    new CollectionDescriptionCriteria(),
    new CollectionTargetGroupsCriteria(),
    new CollectionOwnerCriteria()
  ];
  private readonly allAppsOfFollowedCollections =
    this.collectionUIService.allAppsOfFollowedCollections;
  private readonly allApps: Signal<AppInterface[]> = computed(() =>
    Array.from(
      new Set(
        this.widgetUIService.apps().concat(this.allAppsOfFollowedCollections()),
      ),
    ),
  );
  private readonly allCollectionFinders: Signal<CollectionFinderItemDTO[]> =
    computed(() => this.collectionUIService.collectionFinderList());

  constructor(
    private platformService: PlatformService,
    private appsSearchService: AppsSearchService,
    private collectionsSearchService: CollectionsSearchService,
    private widgetUIService: WidgetUIService,
    private collectionUIService: CollectionUIService,
    private folderUIService: FolderUiService,
    private appFinderApiService: AppFinderApiService,
    private collectionApiService: CollectionApiService,
  ) {}

  loadSearchTranslations(query: string): void {
    if (!query.trim() || this.translationsRequested) return;
    this.translationsRequested = true;

    forkJoin({
      apps: this.appFinderApiService.getApps(undefined, true),
      collections: this.collectionApiService.listFinder(true, true),
    }).subscribe({
      next: ({ apps, collections }) => {
        this.appTranslations.set(new Map(apps.map(app =>
          [String(app.appId), app.translatedDescriptions ?? []])));
        this.collectionTranslations.set(new Map(collections.map(collection =>
          [String(collection.id), collection.translatedDescriptions ?? []])));
      },
      error: () => { this.translationsRequested = false; },
    });
  }

  searchStartPage(_query: string): StartPageSearchResults {
    _query = _query.trim().toLocaleLowerCase();
    const appTranslations = this.appTranslations();
    const collectionTranslations = this.collectionTranslations();
    const searchableApps = this.getSearchableApps(this.allApps().map(app => ({
      ...app,
      translatedDescriptions: appTranslations.get(String(app.appId)) ?? app.translatedDescriptions,
    })));
    const searchableCollections = this.getSearchableCollections(
      this.allCollectionFinders().map(collection => ({
        ...collection,
        translatedDescriptions: collectionTranslations.get(String(collection.id)) ?? collection.translatedDescriptions,
      })),
    );

    const searchResult = this.mapToSearchResults(
      this.appsSearchService.search(
        searchableApps,
        _query,
        this.appSearchCriteria,
      ),
      this.collectionsSearchService.search(
        searchableCollections,
        _query,
        this.collectionSearchCriteria,
      ),
    );

    return searchResult;
  }

  private getSearchableApps(_apps: AppInterface[]): SearchableAppInterface[] {
    return _apps.filter((_app) => {
      if (this.platformService.isMobile()) {
        return _app.isActive && _app.supportsMobile;
      } else {
        return _app.isActive;
      }
    }) as SearchableAppInterface[];
  }

  private getSearchableCollections(
    _collections: CollectionFinderItemDTO[],
  ): SearchableCollectionInterface[] {
    return _collections.filter((collection) => {
      return this.platformService.isMobile()
        ? collection.supportsMobile
        : collection.supportsDesktop;
    }) as SearchableCollectionInterface[];
  }

  private mapToSearchResults(
    _appsFound: AppInterface[],
    _collectionFound: CollectionFinderItemDTO[],
  ): StartPageSearchResults {

  
    const startPageResults: StartPageSearchableAppInterface[] = [];
    const appFinderResults: AppInterface[] = [];
    const collectionFinderResults: CollectionFinderItemDTO[] = [];
    let mixFinderItems = [
      ...(_appsFound as SearchableAppInterface[]),
      ...(_collectionFound as SearchableCollectionInterface[]),
    ];

    // Process bookmarks, folder apps and followed collection apps
    _appsFound.forEach((foundApp) => {
      const isFolderApp = !!foundApp.isBookmark || !!foundApp.inDashboard;
      const isFollowedCollectionApp = this.allAppsOfFollowedCollections().some(
        (collectionApp) => {
          return collectionApp.appId === foundApp.appId;
        },
      );

      if (!isFolderApp && !isFollowedCollectionApp) {
        return;
      }

      const startPageApp: StartPageSearchableAppInterface = {
        ...(foundApp as SearchableAppInterface),
      };

      if (isFolderApp) {
        startPageApp.folderName = this.folderUIService.getFolderName(foundApp);
      }

      if (isFollowedCollectionApp) {
        startPageApp.collectionName = this.collectionUIService.getCollectionName(foundApp);
      }

      startPageResults.push(startPageApp);
    });

    // Exclude Start Page apps from App Finder items
    mixFinderItems = mixFinderItems.filter(
      (mixItem) =>
        !startPageResults.some(
          (spItem) => spItem.appId === (mixItem as AppInterface).appId,
        ),
    );

    // Process mixFinderItems for appFinders and collectionFinders
    mixFinderItems.forEach((mixFinderItem) => {
      if ('appId' in mixFinderItem) {
        appFinderResults.push(mixFinderItem);
      } else {
        collectionFinderResults.push(mixFinderItem as CollectionFinderItemDTO);
      }
    });

    return {
      appFinder: (appFinderResults as SearchableAppInterface[]).filter((_app) => !_app.isBookmark),
      collectionFinder: (collectionFinderResults as SearchableCollectionInterface[]),
      startPage: startPageResults,
    };
  }
}
